import { useState } from 'react';
import StepWrapper from '../stepWrapper';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import styled from 'styled-components';
import {
  setTab,
  OrderFlowTabs,
  setExtendedInsurance,
  OrderFlow,
  setLegalConsentOne,
  setNewsConsent
} from '../../../redux/orderFlow/orderFlowSlice';
import { CheckBoxGroup, Box } from 'grommet';
import { MEDIA_URL } from '../../template/main-template/main-templateStyles';
import Tooltip from '../../atoms/tooltip/tooltip';
import { Products } from '../../../models/order-details';
import { StepFormWrapper } from '../../molecules/form-tab-presenter/form-tabStyles';
import { useTranslation } from 'react-i18next';
import { navigateToNextTab } from '../../../utils/navigation-helpers';
import { RootState } from '../../../redux/store';

const ExtendedInsurance = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentFlow = useAppSelector(state => state.orderFlow.flow);
  const orderedProducts = useAppSelector(
    state => state.orderFlow.orderedProducts
  );
  const flow2Products = useAppSelector(state => state.orderFlow.flow2Products);

  const apiProductsResponse = useAppSelector(
    (state: RootState) => state.products.products.response
  );
  const apiProducts = Array.isArray(apiProductsResponse)
    ? apiProductsResponse
    : [];

  const getUserProducts = () => {
    if (currentFlow === OrderFlow.Flow2 && flow2Products) {
      try {
        const parsedProducts = JSON.parse(flow2Products);
        if (Array.isArray(parsedProducts) && Array.isArray(apiProducts)) {
          return parsedProducts
            .map((dbId: string) => {
              const matchedProduct = apiProducts.find(
                (product: any) => product.id === dbId
              );
              return matchedProduct ? matchedProduct.productId : dbId;
            })
            .filter(Boolean);
        }
        return [];
      } catch (error) {
        console.error('Error parsing flow2Products:', error);
        return [];
      }
    }
    return orderedProducts || [];
  };

  const userProducts = getUserProducts();

  const availableProducts = apiProducts.filter(product => {
    if (currentFlow === OrderFlow.Flow1) {
      return true;
    }

    const productId = product.productId || product.id;
    return !userProducts.some(userProduct => {
      if (typeof userProduct === 'string') {
        return (
          userProduct === productId ||
          (userProduct.includes('SPORTS') && productId?.includes('SPORTS')) ||
          (userProduct.includes('TRAVEL') && productId?.includes('TRAVEL'))
        );
      }
      return false;
    });
  });

  const sportsProduct = availableProducts.find(product =>
    product.productId?.includes('SPORTS')
  );
  const travelProduct = availableProducts.find(product =>
    product.productId?.includes('TRAVEL')
  );

  const hasSportsProduct = userProducts.some(
    product => typeof product === 'string' && product.includes('SPORTS')
  );
  const hasHolidayProduct = userProducts.some(
    product => typeof product === 'string' && product.includes('TRAVEL')
  );

  const sportsPrice = sportsProduct?.priceWithEHIC;
  const sportsPriceWithoutEHIC = sportsProduct?.priceWithoutEHIC;
  const travelPrice = travelProduct?.priceWithEHIC;
  const travelPriceWithoutEHIC = travelProduct?.priceWithoutEHIC;

  function convertStringToEnum(
    products: string[] | undefined
  ): Products | null {
    if (!products || products.length === 0) return null;

    const hasMain = products.some(p => p.includes('MAIN'));
    const hasSports = products.some(p => p.includes('SPORTS'));
    const hasTravel = products.some(p => p.includes('TRAVEL'));

    if (hasMain) {
      if (hasSports && hasTravel) {
        return Products.MainSportsHolidays;
      } else if (hasSports) {
        return Products.MainSports;
      } else if (hasTravel) {
        return Products.MainHolidays;
      } else {
        return Products.Main;
      }
    }
    return null;
  }

  function showSportsPackage(): boolean {
    if (currentFlow === OrderFlow.Flow1) {
      return !!sportsProduct;
    } else if (currentFlow === OrderFlow.Flow2) {
      return !!sportsProduct && !hasSportsProduct;
    }
    return false;
  }

  function showHolidayPackage(): boolean {
    if (currentFlow === OrderFlow.Flow1) {
      return !!travelProduct;
    } else if (currentFlow === OrderFlow.Flow2) {
      return !!travelProduct && !hasHolidayProduct;
    }
    return false;
  }

  const options = [
    showSportsPackage() && {
      id: 'checkbox-sportsPackage',
      label: t('checkbox-sportsPackage', { PRICE: sportsPrice }),
      value: 'sports'
    },
    showHolidayPackage() && {
      id: 'checkbox-showHolidayPackage',
      label: t('checkbox-holidayPackage', { PRICE: travelPrice }),
      value: 'holiday'
    }
  ].filter(Boolean);

  const [value, setValue] = useState<string[]>([]);

  const handleContinue = () => {
    const sportsPackage = value.includes('sports');
    const holidayPackage = value.includes('holiday');

    const sportsProductId = sportsProduct?.productId;
    const travelProductId = travelProduct?.productId;

    dispatch(
      setExtendedInsurance({
        sportsPackage,
        holidayPackage,
        sportsProductId,
        travelProductId,
        isFlow2: currentFlow === OrderFlow.Flow2
      })
    );
    navigateToNextTab(dispatch, OrderFlowTabs.OrderSummary);
  };

  const handleBack = () => {
    if (currentFlow === OrderFlow.Flow1) {
      navigateToNextTab(dispatch, OrderFlowTabs.HealthCard);
    } else {
      dispatch(setLegalConsentOne({ legalConsentOne: false }));
      dispatch(setNewsConsent({ newsConsent: false }));
      navigateToNextTab(dispatch, OrderFlowTabs.PersonalDetailsConfirmation);
    }
  };

  const nextLabel = value.length > 0 ? 'NEXT' : 'NOT-NOW';

  return !showSportsPackage() && !showHolidayPackage() ? (
    <StepFormWrapper>
      <OptionalText>{t('form-sub-tab-5-allextended')}</OptionalText>
    </StepFormWrapper>
  ) : (
    <StepWrapper
      next={{ f: handleContinue, label: nextLabel }}
      back={{ f: handleBack, label: 'BACK' }}
    >
      {showSportsPackage() && (
        <Tooltip
          children={
            <Pack>
              <PackImage src={`${MEDIA_URL}/pack_sports.webp`} />
              <PackText>{t('form-sub-tab-5-sports')}</PackText>
            </Pack>
          }
          tooltipText={t('tooltip-sport')}
        />
      )}
      {showHolidayPackage() && (
        <Tooltip
          children={
            <Pack>
              <PackImage src={`${MEDIA_URL}/pack_holiday.webp`} />
              <PackText>{t('form-sub-tab-5-holiday')}</PackText>
            </Pack>
          }
          tooltipText={t('tooltip-holiday')}
        />
      )}
      <Box align='center'>
        <CheckBoxGroupStyled
          align='left'
          options={options.map((option: any) => option)}
          gap='xsmall'
          value={value}
          onChange={(e: any) => setValue(e.value)}
        />
        <ExtraText>{t('checkbox-extraText')}</ExtraText>
      </Box>
    </StepWrapper>
  );
};

export default ExtendedInsurance;
const ExtraText = styled.p`
  color: white;
  font-size: 14px;
`;
const OptionalText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 800;
`;
const PackText = styled.p`
  color: white;
  line-height: 1.5;
`;
const PackImage = styled.img`
  height: 99px;
`;
const Pack = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const CheckBoxGroupStyled = styled(CheckBoxGroup)`
  svg {
    stroke: #06b192;
  }
  div {
    div {
      border: 2px solid #06b192;
      border-radius: 10%;
      align: center;
    }
  }
  label {
    color: white;
    font-size: 18px;
    div {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }
`;
